import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { stock as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { basculer, usePanneau } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './Creances.css';
import './VentesJour.css';  // rg-* du regroupement
import './Stock.css';
import { exporterXlsx } from './exportXlsx';
import { useBi } from '../BiContext';
import apiBi from '../services/api';

// ─── Stock VN / VO — photo du jour par concession ────────────────────────────
// Trois blocs. Le premier (2026-09-16) : une ligne par société avec le volume,
// la valorisation (prix d'achat) et la ventilation par ancienneté, en véhicules
// ou en euros (bascule). Même vocabulaire visuel que les Créances (tranches
// colorées du vert au rouge) : plus c'est vieux, plus c'est rouge.
// Le deuxième : le stock par marque, modèle ou âge, dont les lignes filtrent
// le troisième — le détail véhicule par véhicule (2026-09-17, séparé le
// 2026-09-30). Le tableau par société dit « combien et depuis quand », jamais
// « lesquels », qui est pourtant la question suivante devant un stock dormant.
// `branche` sélectionne le mart (Antilles par défaut, Auto Métropole).

const HUES = ['#0F766E', '#3E8F5F', '#C9A84C', '#DE9A2E', '#D6742B', '#C0472F', '#8E2323'];
const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];
const nf = new Intl.NumberFormat('fr-FR');
const cM = (v) => {
  v = Math.round(v || 0);
  const a = Math.abs(v);
  if (a >= 1e6) return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v / 1e6) + ' M€';
  if (a >= 1e3) return nf.format(Math.round(v / 1e3)) + ' k€';
  return v + ' €';
};
const kc = (v) => (!v ? '·' : nf.format(Math.round(v / 1000)) + ' k');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const TITRE = { vn: 'VN — Stock', vo: 'VO — Stock' };
const BRANCHE_NOM = { 'auto-antilles': 'Antilles', 'auto-metropole': 'Auto Métropole' };
const jour = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

// Colonnes du détail. `tri` = accesseur de tri ; absent = colonne non triable.
// `vnSeul` : colonne retirée du rapport VO, où elle ne peut RIEN porter (cf.
// `colonnes()` plus bas) — afficher 816 tirets ferait douter du rapport entier.
const COLS = [
  { c: 'societe_nom', l: 'Société', tri: (v) => v.societe_nom || '' },
  { c: 'marque', l: 'Marque', tri: (v) => v.marque || '' },
  { c: 'modele_ligne', l: 'Modèle', tri: (v) => v.modele_ligne || '' },
  { c: 'version', l: 'Modèle détaillé', tri: (v) => v.version || '', vnSeul: true },
  // Type de stock du véhicule (2026-10-01, VN) : le même libellé que l'axe
  // « Type de stock » du bloc 2.
  { c: 'typestock', l: 'Type de stock', tri: (v) => v.typestock || '', vnSeul: true },
  { c: 'couleur', l: 'Couleur', tri: (v) => v.couleur || '' },
  { c: 'chassis', l: 'Châssis', mono: true },
  { c: 'jours_en_stock', l: 'Âge', num: true, tri: (v) => v.jours_en_stock ?? -1 },
  // Offres (2026-10-24, import manuel dans A97 DATA) : la dernière offre faite
  // sur le véhicule et le nombre d'affaires (une affaire = un prospect ; elle
  // compte 1,8 offre en moyenne, versions remplacées comprises).
  { c: 'derniere_offre', l: 'Dernière offre', num: true, tri: (v) => v.derniere_offre || '', offres: true },
  { c: 'affaires', l: 'Affaires', num: true, tri: (v) => v.affaires ?? -1, offres: true },
  // Dernière vente du même modèle sur le périmètre (2026-09-30) : un véhicule
  // dont le modèle ne se vend plus est celui sur lequel agir.
  { c: 'derniere_modele', l: 'Dernière vente du modèle', num: true, tri: (v) => v.derniere_modele || '', vnSeul: true },
  { c: 'immat', l: 'Immat', mono: true, tri: (v) => v.immat || '' },
  { c: 'date_mec', l: 'Date MEC', tri: (v) => v.date_mec || '' },
];

// L'export VO ne distingue pas ligne de modèle et finition : sa désignation
// complète tient dans « Modèle ». La colonne détaillée n'a donc pas de sens
// ici, et on le dit en note plutôt que de la laisser vide.
const colonnes = (type, avecOffres) => COLS.filter((c) => (!c.vnSeul || type === 'vn') && (!c.offres || avecOffres));
// Sans offre depuis 90 jours (ou jamais), sur un véhicule dont la société a
// ses offres importées. ⚠ Un fichier pas encore importé (Blue…) ne rend pas
// ses véhicules « sans offre » : ils sont « non importés ».
const SEUIL_OFFRE = 90;
const sansOffre = (v) => !v.derniere_offre || ilYa(v.derniere_offre) > SEUIL_OFFRE;

// Deux blocs sous le tableau par concession (2026-09-30) :
//   • bloc 2 — le stock regroupé par marque, modèle ou tranche d'âge. Chaque
//     ligne est un FILTRE : cliquer une ou plusieurs lignes restreint le bloc 3.
//     Les filtres des trois axes se cumulent (une marque ET une tranche d'âge) ;
//     chaque axe est calculé sur les filtres des AUTRES axes, pour que ses
//     propres lignes restent toutes visibles et cliquables ;
//   • bloc 3 — le détail véhicule par véhicule, avec la recherche libre et
//     l'export Excel de ce qui est affiché.
const AXES_STOCK = [
  { c: 'marque', l: 'Marque', cle: (v) => v.marque || '— NR' },
  // Un modèle n'est lisible qu'avec sa marque (« 208 » seul, ou en VO une
  // désignation qui ne la cite pas toujours).
  { c: 'modele', l: 'Modèle', cle: (v) => `${v.marque || '— NR'} · ${v.modele_ligne || '— NR'}` },
  // Type de stock (2026-09-30) : VN, VD, VN-A affecter… — même libellé côté
  // véhicules (typestock) et côté ventes (type de stock de la vente).
  { c: 'type', l: 'Type de stock', cle: (v) => v.typestock || '— NR' },
  { c: 'age', l: 'Âge de stock', cle: (v) => v.tranche },
];
const NOMS_GROUPES = { marque: ['marque', 'marques'], modele: ['modèle', 'modèles'], type: ['type de stock', 'types de stock'], age: ["tranche d'âge", "tranches d'âge"] };
// Un seul état de filtrage (CLAUDE.md §4 bis) : les lignes retenues vivent
// dans l'URL — `marques`, `modeles`, `ages` — et se reflètent dans le panneau
// de droite. Séparateur « | » : une désignation VO peut contenir une virgule.
const CLE_URL = { marque: 'marques', modele: 'modeles', type: 'types', age: 'ages' };
const SEP = '|';
const lireFiltres = (params) => Object.fromEntries(AXES_STOCK.map((ax) => {
  const brut = (params.get(CLE_URL[ax.c]) || '').split(SEP).filter(Boolean);
  return [ax.c, ax.c === 'age' ? brut.map(Number).filter(Number.isInteger) : brut];
}));
// Ventes en regard du stock (2026-09-30) : « il y a N j », et la couverture
// = stock ÷ ventes mensuelles moyennes des 90 derniers jours. Au-delà de six
// mois, ou sans aucune vente sur 90 jours, la ligne est à traiter : c'est
// l'exception qu'on marque (rouge), pas la norme.
const SEUIL_COUVERTURE = 6;
// « dont VD » (2026-10-01, VN) : même règle que le serveur (EST_VD de
// services/stock.js) — un type de stock commençant par VD.
const estVd = (v) => /^VD/i.test((v.typestock || '').trim());
const ilYa = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(`${iso}T12:00:00`).getTime()) / 86400000)) : null);
const derniereVente = (iso) => (iso ? `${jour(iso)} · il y a ${nf.format(ilYa(iso))} j` : 'aucune');
const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const passe = (v, filtres, sauf) => AXES_STOCK.every((ax) =>
  ax.c === sauf || !filtres[ax.c].length || filtres[ax.c].includes(ax.cle(v)));

/**
 * Blocs 2 et 3 : chargés à part de la photo agrégée — le tableau par société
 * doit s'afficher tout de suite, ce détail peut arriver une seconde plus tard.
 * Tri, recherche et regroupements à l'écran — le périmètre complet tient en
 * ~2 000 lignes, un aller-retour serveur par geste serait du gâchis.
 */
function DetailStock({ type, branche, selSet, labels, dateArrete, filtres, setFiltres }) {
  const [lignes, setLignes] = useState(null);
  const [ventes, setVentes] = useState([]);
  const [offres, setOffres] = useState({ dispo: false, societes: [] });
  const [erreur, setErreur] = useState('');
  const [axe, setAxe] = useState('marque');
  // Tri du bloc 2 : par défaut le plus gros stock d'abord (l'âge, dans
  // l'ordre des tranches). Trier par couverture fait remonter ce qui est à
  // traiter.
  const [triG, setTriG] = useState(null);   // { c: 'n' | 'v90' | 'couv' | 'derniere', desc }

  useEffect(() => {
    let actif = true;
    setLignes(null); setErreur('');
    api.detail(type, branche)
      .then((r) => { if (actif) { setLignes(r.data.vehicules || []); setVentes(r.data.ventes || []); setOffres(r.data.offres || { dispo: false, societes: [] }); } })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Détail indisponible'); });
    return () => { actif = false; };
  }, [type, branche]);

  const offresSoc = useMemo(() => new Set(offres.societes || []), [offres]);
  const avecOffres = !!offres.dispo;
  // Périmètre sociétés (bloc 1 / panneau de filtres).
  const perimetre = useMemo(() => (lignes || []).filter((v) => selSet.has(v.societe)), [lignes, selSet]);
  const ventesPerim = useMemo(() => ventes.filter((v) => selSet.has(v.societe)), [ventes, selSet]);
  // Dernière vente de chaque modèle sur le périmètre (bloc 3 et export).
  const derniereParModele = useMemo(() => {
    const m = new Map();
    const cle = AXES_STOCK.find((x) => x.c === 'modele').cle;
    ventesPerim.forEach((v) => { const k = cle(v); if (v.derniere && (!m.has(k) || v.derniere > m.get(k))) m.set(k, v.derniere); });
    return m;
  }, [ventesPerim]);

  // Ventes en regard : partout, sauf sur le modèle VO — sa « désignation »
  // est quasi unique par véhicule, la couverture n'y mesurerait rien (358
  // lignes rouges sur 425 aux Antilles).
  const avecVentes = !(type === 'vo' && axe === 'modele');
  // Bloc 2 : les lignes de l'axe courant, sur les filtres des autres axes.
  const groupes = useMemo(() => {
    const ax = AXES_STOCK.find((x) => x.c === axe);
    const m = new Map();
    for (const v of perimetre) {
      if (!passe(v, filtres, axe)) continue;
      const k = ax.cle(v);
      const g = m.get(k) || { cle: k, n: 0, vd: 0, valeur: 0, valorises: 0, jours: 0, avecAge: 0, vieux: 0, couverts: 0, sansOffre: 0 };
      g.n += 1; if (estVd(v)) g.vd += 1;
      if (offresSoc.has(v.societe)) { g.couverts += 1; if (sansOffre(v)) g.sansOffre += 1; }
      if (Number(v.valeur_achat)) { g.valeur += Number(v.valeur_achat); g.valorises += 1; }
      if (v.jours_en_stock != null) { g.jours += v.jours_en_stock; g.avecAge += 1; }
      if (v.tranche === labels.length - 1) g.vieux += 1;
      m.set(k, g);
    }
    // Ventes : mêmes filtres (règle 4), clées comme le stock ; une vente sans
    // âge connu ne se range dans aucune tranche.
    const vt = new Map();
    for (const v of ventesPerim) {
      if (!passe(v, filtres, axe)) continue;
      const k = ax.cle(v);
      if (axe === 'age' && (k == null)) continue;
      const x = vt.get(k) || { v90: 0, derniere: null };
      x.v90 += v.v90;
      if (v.derniere && (!x.derniere || v.derniere > x.derniere)) x.derniere = v.derniere;
      vt.set(k, x);
    }
    const out = [...m.values()].map((g) => {
      const x = vt.get(g.cle) || { v90: 0, derniere: null };
      return {
        ...g, v90: x.v90, derniere: x.derniere,
        // En mois ; Infinity = aucune vente sur 90 jours (le plus urgent).
        couv: x.v90 > 0 ? g.n / (x.v90 / 3) : Infinity,
        libelle: axe === 'age' ? (labels[g.cle] || '—') : g.cle,
        ageMoyen: g.avecAge ? Math.round(g.jours / g.avecAge) : null,
      };
    });
    if (triG && (avecVentes || triG.c === 'sansOffre')) {
      const val = (g) => (triG.c === 'derniere' ? (g.derniere || '') : g[triG.c]);
      return out.sort((a, b) => {
        const x = val(a), y = val(b);
        const c = typeof x === 'string' ? x.localeCompare(y) : (x === y ? 0 : x > y ? 1 : -1);
        return (triG.desc ? -c : c) || b.n - a.n;
      });
    }
    // Âge : dans l'ordre des tranches ; sinon, le plus gros stock d'abord.
    return axe === 'age' ? out.sort((a, b) => a.cle - b.cle)
      : out.sort((a, b) => b.n - a.n || String(a.libelle).localeCompare(String(b.libelle), 'fr'));
  }, [perimetre, ventesPerim, filtres, axe, labels, triG, avecVentes, offresSoc]);

  // Bloc 3 : tous les filtres.
  const filtrees = useMemo(() => perimetre.filter((v) => passe(v, filtres, null)), [perimetre, filtres]);

  // Recliquer une ligne retenue la retire (règle 2) ; plusieurs lignes d'un
  // même axe se cumulent (« PEUGEOT ou OPEL »), les axes entre eux se croisent.
  const basculerLigne = (cle) => setFiltres({
    ...filtres, [axe]: filtres[axe].includes(cle) ? filtres[axe].filter((x) => x !== cle) : [...filtres[axe], cle],
  });
  const retirer = (ax, cle) => setFiltres({ ...filtres, [ax]: filtres[ax].filter((x) => x !== cle) });
  const nbFiltres = AXES_STOCK.reduce((n, ax) => n + filtres[ax.c].length, 0);
  const totalGroupes = groupes.reduce((n, g) => n + g.n, 0);
  // « dont VD » sur les axes marque et modèle, en VN seulement (pas de VD en VO).
  const avecVd = type === 'vn' && (axe === 'marque' || axe === 'modele');
  const libAxe = AXES_STOCK.find((x) => x.c === axe)?.l;
  const libelleFiltre = (ax, cle) => (ax === 'age' ? labels[cle] || '—' : cle);

  return (
    <>
      <div className="cr-card">
        <div className="cr-card-h st-card-h">
          <h2>Stock par {libAxe.toLowerCase()}</h2>
          <div className="st-tools">
            <div className="st-seg" role="group" aria-label="Regrouper le stock par">
              {AXES_STOCK.map((x) => (
                <button key={x.c} type="button" className={axe === x.c ? 'on' : ''} onClick={() => { setAxe(x.c); setTriG(null); }}>
                  {x.l}{filtres[x.c].length > 0 && <span className="st-seg-nb">{filtres[x.c].length}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="st-sous-h">
          <span className="cr-hint">
            {!lignes ? 'chargement…'
              : `${nf.format(groupes.length)} ${NOMS_GROUPES[axe][groupes.length > 1 ? 1 : 0]} · ${nf.format(totalGroupes)} véhicules ${avecVentes ? `· couverture = stock ÷ ventes mensuelles moyennes des 90 derniers jours, en rouge au-delà de ${SEUIL_COUVERTURE} mois` : '· ventes non rapprochées : la désignation VO est propre à chaque véhicule'}`}
          </span>
        </div>
        {erreur && <div className="cr-card-b"><p className="st-note">{erreur}</p></div>}
        {!lignes && !erreur && <div className="cr-card-b"><div className="cr-skel" /></div>}
        {lignes && !erreur && (
          <div className="cr-scroll st-detail-scroll st-groupes-scroll">
            <table className="cr-tbl st-detail st-groupes">
              <thead><tr>
                <th>{libAxe}</th>
                {[['n', 'Véhicules'], ...(avecVd ? [['vd', 'dont VD']] : []), ...(avecVentes ? [...(axe !== 'age' ? [['derniere', 'Dernière vente']] : []), ['v90', 'Ventes 3 mois'], ['couv', 'Couverture']] : []), ...(avecOffres ? [['sansOffre', `Sans offre ${SEUIL_OFFRE} j`]] : [])].map(([c, l]) => (
                  <th key={c} className={`num st-triable${triG?.c === c ? ' st-tri-on' : ''}`} title="Trier"
                    onClick={() => setTriG((t) => (t?.c === c ? (t.desc ? { c, desc: false } : null) : { c, desc: c !== 'derniere' }))}>
                    {l}{triG?.c === c && <span className="st-tri-fleche">{triG.desc ? '▾' : '▴'}</span>}
                  </th>
                ))}
                <th className="num">Part</th>
                <th className="num">Valeur d'achat</th>
                <th className="num">Âge moyen</th>
                {axe !== 'age' && <th className="num">{labels[labels.length - 1] || 'Dormants'}</th>}
              </tr></thead>
              <tbody>
                {groupes.map((g) => {
                  const on = filtres[axe].includes(g.cle);
                  return (
                    <tr key={String(g.cle)} className={`tbl-clic${on ? ' actif' : ''}`} onClick={() => basculerLigne(g.cle)}
                      title={on ? 'Retirer ce filtre' : 'Filtrer le détail sur cette ligne'}>
                      <td className="st-long" title={String(g.libelle)}>{g.libelle}</td>
                      <td className="num strong">{nf.format(g.n)}</td>
                      {avecVd && <td className={`num${g.vd ? '' : ' muted'}`}>{g.vd ? nf.format(g.vd) : '·'}</td>}
                      {avecVentes && axe !== 'age' && (
                        <td className={`num${!g.derniere || ilYa(g.derniere) > 90 ? ' tbl-neg' : ''}`}>{derniereVente(g.derniere)}</td>
                      )}
                      {avecVentes && <>
                        <td className={`num${g.v90 > 0 ? '' : ' muted'}`}>{g.v90 > 0 ? nf.format(g.v90) : '·'}</td>
                        <td className={`num${g.couv > SEUIL_COUVERTURE ? ' tbl-neg' : ''}`}>
                          {g.couv === Infinity ? 'aucune vente en 90 j' : `${nf1.format(g.couv)} mois`}
                        </td>
                      </>}
                      {avecOffres && (
                        <td className={`num${g.couverts ? '' : ' muted'}`}
                          title={g.couverts ? `${g.sansOffre} véhicule(s) sur ${g.couverts} sans offre depuis ${SEUIL_OFFRE} jours${g.couverts < g.n ? ` · ${g.n - g.couverts} sans offres importées` : ''}` : 'offres non importées pour ces sociétés'}>
                          {g.couverts ? (g.sansOffre ? `${nf.format(g.sansOffre)} · ${pct(g.sansOffre, g.couverts)} %` : '·') : '—'}
                        </td>
                      )}
                      <td className="num">{pct(g.n, totalGroupes)} %</td>
                      <td className="num">{g.valorises ? cM(g.valeur) : '—'}{g.valorises > 0 && g.valorises < g.n && <sup className="st-partiel-sup" title={`${g.n - g.valorises} véhicule(s) sans valeur d'achat`}>*</sup>}</td>
                      <td className="num">{g.ageMoyen == null ? '—' : `${nf.format(g.ageMoyen)} j`}</td>
                      {axe !== 'age' && <td className={`num${g.vieux ? '' : ' muted'}`}>{g.vieux ? `${nf.format(g.vieux)} · ${pct(g.vieux, g.n)} %` : '—'}</td>}
                    </tr>
                  );
                })}
                {groupes.length === 0 && (
                  <tr><td colSpan={10} className="muted st-vide">Aucun véhicule dans la sélection.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DetailVehicules type={type} lignes={lignes} erreur={erreur} filtrees={filtrees} labels={labels} dateArrete={dateArrete}
        offres={offres} offresSoc={offresSoc}
        derniereDe={(v) => derniereParModele.get(AXES_STOCK.find((x) => x.c === 'modele').cle(v)) || null}
        puces={AXES_STOCK.flatMap((ax) => filtres[ax.c].map((cle) => ({ ax: ax.c, lib: ax.l, cle, valeur: libelleFiltre(ax.c, cle) })))}
        onRetirer={retirer} onToutRetirer={() => setFiltres({ marque: [], modele: [], type: [], age: [] })} nbFiltres={nbFiltres} />
    </>
  );
}

// Bloc 3 : la liste véhicule par véhicule, recherche libre et export Excel.
function DetailVehicules({ type, lignes, erreur, filtrees, labels, dateArrete, derniereDe, puces, onRetirer, onToutRetirer, nbFiltres, offres = {}, offresSoc = new Set() }) {
  const avecOffres = !!offres.dispo;
  const [q, setQ] = useState('');
  const [tri, setTri] = useState({ c: 'jours_en_stock', desc: true });

  const visibles = useMemo(() => {
    const m = q.trim().toLowerCase();
    const avecVente = filtrees.map((v) => ({ ...v, derniere_modele: derniereDe ? derniereDe(v) : null }));
    const dans = (v) => [v.marque, v.modele_ligne, v.version, v.typestock, v.couleur, v.chassis, v.immat, v.societe_nom]
      .some((x) => (x || '').toLowerCase().includes(m));
    const out = m ? avecVente.filter(dans) : avecVente;
    const col = COLS.find((x) => x.c === tri.c);
    if (col?.tri) {
      out.sort((a, b) => {
        const x = col.tri(a); const y = col.tri(b);
        const c = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'fr');
        return tri.desc ? -c : c;
      });
    }
    return out;
  }, [filtrees, q, tri]);   // eslint-disable-line react-hooks/exhaustive-deps

  const trier = (c) => setTri((t) => (t.c === c ? { c, desc: !t.desc } : { c, desc: c === 'jours_en_stock' }));
  const cols = useMemo(() => colonnes(type, avecOffres), [type, avecOffres]);

  // Une colonne vide partout fait douter du rapport entier : on le dit.
  const manquants = useMemo(() => {
    if (!visibles.length) return [];
    return (type === 'vn' ? ['version', 'immat', 'date_mec'] : ['date_mec'])
      .map((c) => ({ c, l: COLS.find((x) => x.c === c).l, n: visibles.filter((v) => !v[c]).length }))
      .filter((x) => x.n > 0);
  }, [visibles, type]);

  // L'export suit ce qui est AFFICHÉ (filtres, recherche, tri) : exporter autre
  // chose que ce qu'on regarde est un piège. Il ajoute la tranche d'âge et la
  // valeur d'achat, utiles dans un tableur, absentes de l'écran faute de place.
  const { exportExcel } = useBi(); // droit « Export Excel » (Administration ▸ Accès)
  const exporter = async () => {
    try { await apiBi.post('/bi/exports', { rapport: `Stock ${type.toUpperCase()}`, tableau: 'Détail des véhicules', lignes: visibles.length }); } catch (e) { return; }
    const xl = [
      { l: 'Société', v: (v) => v.societe_nom, largeur: 30 },
      { l: 'Marque', v: (v) => v.marque, largeur: 16 },
      { l: 'Modèle', v: (v) => v.modele_ligne, largeur: 30 },
      ...(type === 'vn' ? [{ l: 'Modèle détaillé', v: (v) => v.version, largeur: 40 }, { l: 'Type de stock', v: (v) => v.typestock, largeur: 24 }] : []),
      { l: 'Couleur', v: (v) => v.couleur, largeur: 18 },
      { l: 'Châssis', v: (v) => v.chassis, largeur: 20 },
      { l: 'Âge (jours)', v: (v) => v.jours_en_stock, type: 'nombre', largeur: 11 },
      { l: "Tranche d'âge", v: (v) => labels[v.tranche], largeur: 14 },
      ...(avecOffres ? [
        { l: 'Dernière offre', v: (v) => (offresSoc.has(v.societe) ? v.derniere_offre : null), type: 'date', largeur: 14 },
        { l: 'Affaires', v: (v) => (offresSoc.has(v.societe) ? v.affaires || 0 : null), type: 'nombre', largeur: 10 },
        { l: 'Offres', v: (v) => (offresSoc.has(v.societe) ? v.offres || 0 : null), type: 'nombre', largeur: 10 },
      ] : []),
      ...(type === 'vn' ? [{ l: 'Dernière vente du modèle', v: (v) => v.derniere_modele, type: 'date', largeur: 14 }] : []),
      { l: 'Immat', v: (v) => v.immat, largeur: 12 },
      { l: 'Date MEC', v: (v) => v.date_mec, type: 'date', largeur: 12 },
      { l: "Valeur d'achat HT (€)", v: (v) => (Number(v.valeur_achat) ? Number(v.valeur_achat) : null), type: 'nombre', largeur: 18 },
    ];
    exporterXlsx(`stock_${type}_${(dateArrete || '').replace(/\//g, '-') || 'jour'}.xlsx`, `Stock ${type.toUpperCase()}`, xl, visibles);
  };

  return (
    <div className="cr-card">
      <div className="cr-card-h st-card-h">
        <h2>Détail des véhicules</h2>
        <div className="st-tools st-tools-detail">
          <input
            className="st-rech" type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : marque, modèle, couleur, châssis, immat…"
            aria-label="Rechercher dans le détail des véhicules"
          />
          {exportExcel && <button type="button" className="cr-btn ghost" onClick={exporter} disabled={!visibles.length}
            title="Exporter les véhicules affichés au format Excel">⬇ Exporter (Excel)</button>}
        </div>
      </div>

      <div className="st-sous-h">
        {puces.map((p) => (
          <span className="st-puce" key={`${p.ax}|${p.cle}`}>
            {p.lib} : <b>{p.valeur}</b>
            <button type="button" onClick={() => onRetirer(p.ax, p.cle)} aria-label="Retirer ce filtre" title="Retirer ce filtre">×</button>
          </span>
        ))}
        {nbFiltres > 1 && <button type="button" className="st-lien" onClick={onToutRetirer}>tout retirer</button>}
        <span className="cr-hint">
          {!lignes ? 'chargement…' : `${nf.format(visibles.length)} véhicule${visibles.length > 1 ? 's' : ''}`}
          {/* L'alerte de données manquantes se lit là où on lit le compte de
              véhicules auquel elle se rapporte. */}
          {manquants.length > 0 && ` · données absentes de la source : ${manquants.map((m) => `${m.l} (${nf.format(m.n)})`).join(', ')}`}
          {/* Offres : import manuel — la date dit si elles sont à jour. */}
          {avecOffres && ` · offres importées le ${jour(offres.maj)} (dernière offre du ${jour(offres.derniere)}), en rouge sans offre depuis ${SEUIL_OFFRE} jours`}
        </span>
      </div>

      {erreur && <div className="cr-card-b"><p className="st-note">{erreur}</p></div>}
      {!lignes && !erreur && <div className="cr-card-b"><div className="cr-skel" /></div>}

      {lignes && !erreur && (
        <div className="cr-scroll st-detail-scroll">
          <table className="cr-tbl st-detail">
            <thead><tr>
              {cols.map((col) => (
                <th
                  key={col.c}
                  className={`${col.num ? 'num' : ''}${col.tri ? ' st-triable' : ''}${tri.c === col.c ? ' st-tri-on' : ''}`}
                  onClick={col.tri ? () => trier(col.c) : undefined}
                  title={col.tri ? 'Trier' : undefined}
                >
                  {col.l}{tri.c === col.c && <span className="st-tri-fleche">{tri.desc ? '▾' : '▴'}</span>}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {visibles.map((v, i) => (
                <tr key={`${v.chassis || v.immat || i}-${i}`}>
                  <td title={v.societe_nom || ''}>{v.societe_nom}</td>
                  <td>{v.marque}</td>
                  <td className="st-long" title={v.modele_ligne || ''}>{v.modele_ligne || '—'}</td>
                  {type === 'vn' && (
                    <td className={`st-long${v.version ? '' : ' muted'}`} title={v.version || ''}>{v.version || '—'}</td>
                  )}
                  {type === 'vn' && <td className={v.typestock ? '' : 'muted'}>{v.typestock || '—'}</td>}
                  <td className={`st-coul${v.couleur ? '' : ' muted'}`} title={v.couleur || ''}>{v.couleur || '—'}</td>
                  <td className="st-mono">{v.chassis || '—'}</td>
                  <td className="num">{v.jours_en_stock == null ? '—' : `${nf.format(v.jours_en_stock)} j`}</td>
                  {avecOffres && (offresSoc.has(v.societe) ? <>
                    <td className={`num${sansOffre(v) ? ' tbl-neg' : ''}`}>{v.derniere_offre ? derniereVente(v.derniere_offre) : 'aucune'}</td>
                    <td className={`num${v.affaires ? '' : ' muted'}`} title={v.affaires ? `${v.offres} offre(s), dont ${v.affaires_90j || 0} affaire(s) sur 90 jours` : undefined}>{v.affaires ? nf.format(v.affaires) : '·'}</td>
                  </> : <>
                    <td className="num muted" title="Offres de cette société pas encore importées dans A97 DATA">non importé</td>
                    <td className="num muted">·</td>
                  </>)}
                  {type === 'vn' && <td className={`num${!v.derniere_modele || ilYa(v.derniere_modele) > 90 ? ' tbl-neg' : ''}`}>{derniereVente(v.derniere_modele)}</td>}
                  <td className={`st-mono${v.immat ? '' : ' muted'}`}>{v.immat || '—'}</td>
                  <td className={v.date_mec ? '' : 'muted'}>{jour(v.date_mec) || '—'}</td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr><td colSpan={cols.length} className="muted st-vide">
                  {q || nbFiltres ? 'Aucun véhicule ne correspond à ces filtres.' : 'Aucun véhicule dans la sélection.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Stock({ type = 'vn', branche = 'auto-antilles' }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [unite, setUnite] = useState('n');   // 'n' véhicules | 'v' euros
  // Le panneau commun : replié d'office sur téléphone (ventesUtils.js).
  const [panneauOuvert, togglePanneau] = usePanneau();

  useEffect(() => {
    let actif = true;
    setData(null); setErreur('');
    api.photo(type, branche).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [type, branche]);

  const labels = useMemo(() => data?.labels || [], [data]);
  const societes = useMemo(() => data?.societes || [], [data]);
  const couleurDe = useMemo(() => { const m = {}; societes.forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; }); return m; }, [societes]);

  const societesUrl = params.get('societes') || '';
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societes.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societes]);
  const selSet = new Set(selected);
  const setFiltre = (v) => { const next = new URLSearchParams(params); if (v) next.set('societes', v); else next.delete('societes'); setParams(next, { replace: true }); };
  const toggleSociete = (code) => {
    const suivantes = selSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code];
    setFiltre(suivantes.length === 0 ? '__aucune__' : suivantes.length === societes.length ? '' : suivantes.join(','));
  };
  // Filtres du détail (blocs 2 → 3), portés par l'URL.
  const filtresStock = useMemo(() => lireFiltres(params), [params]);
  const setFiltresStock = (f) => {
    const next = new URLSearchParams(params);
    AXES_STOCK.forEach((ax) => { const l = f[ax.c]; if (l.length) next.set(CLE_URL[ax.c], l.join(SEP)); else next.delete(CLE_URL[ax.c]); });
    setParams(next, { replace: true });
  };
  const nbFiltresStock = AXES_STOCK.reduce((n, ax) => n + filtresStock[ax.c].length, 0);
  // Le panneau ne liste que les lignes RETENUES (cochées) : la liste complète
  // des modèles en compterait plusieurs centaines. Décocher retire le filtre.
  const categoriesStock = AXES_STOCK.flatMap((ax) => filtresStock[ax.c].map((cle) => ({
    code: `${ax.c}${SEP}${cle}`, nom: `${ax.l} : ${ax.c === 'age' ? (data?.labels || [])[cle] || '—' : cle}`,
  })));
  const retirerCategorie = (code) => {
    const [ax, ...reste] = code.split(SEP); const cle = ax === 'age' ? Number(reste.join(SEP)) : reste.join(SEP);
    setFiltresStock({ ...filtresStock, [ax]: filtresStock[ax].filter((x) => x !== cle) });
  };
  const toutReinitialiser = () => {
    const next = new URLSearchParams(params);
    ['societes', ...Object.values(CLE_URL)].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };

  const sel = societes.filter((s) => selSet.has(s.code));
  const total = useMemo(() => {
    const t = { vehicules: 0, valeur: 0, valorises: 0, jours: 0, vd: { n: 0, v: 0 }, tranches: labels.map(() => ({ n: 0, v: 0 })) };
    for (const s of sel) {
      t.vehicules += s.vehicules; t.valeur += s.valeur; t.valorises += s.valorises; t.jours += (s.ageMoyen || 0) * s.vehicules;
      t.vd.n += s.vd?.n || 0; t.vd.v += s.vd?.v || 0;
      s.tranches.forEach((x, i) => { t.tranches[i].n += x.n; t.tranches[i].v += x.v; });
    }
    t.ageMoyen = t.vehicules ? Math.round(t.jours / t.vehicules) : null;
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societesUrl, societes, labels]);

  const Filtres = (
    <FiltresPanel
      ouvert={panneauOuvert} onToggle={togglePanneau}
      societesDispo={societes} societesActives={selected}
      onToggleSociete={toggleSociete}
      onToutesSocietes={() => setFiltre('')}
      onAucuneSociete={() => setFiltre('__aucune__')}
      categoriesDispo={categoriesStock} categoriesActives={categoriesStock.map((c) => c.code)}
      onToggleCategorie={retirerCategorie} titreCategories="Détail des véhicules" categoriesApres
      couleurDe={couleurDe} nbActifs={(societesUrl ? 1 : 0) + nbFiltresStock} onReset={toutReinitialiser}
    />
  );

  // Maille du bloc : concession, site, plaque. Les tranches et les totaux se
  // RE-SOMMENT à chaque niveau ; l'âge moyen, lui, se repondère par le volume —
  // moyenner des âges moyens de concessions inégales donnerait un faux.
  const maille = useRegroupement();
  const feuilles = useMemo(
    () => [...sel].map((x) => ({ ...x, libelle: x.nom })).sort((x, y) => y.vehicules - x.vehicules), [sel]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommerStock = (membres) => {
    const t = { vehicules: 0, valeur: 0, valorises: 0, jours: 0, vd: { n: 0, v: 0 }, tranches: labels.map(() => ({ n: 0, v: 0 })) };
    membres.forEach((m) => {
      t.vehicules += m.vehicules; t.valeur += m.valeur; t.valorises += m.valorises;
      t.vd.n += m.vd?.n || 0; t.vd.v += m.vd?.v || 0;
      t.jours += (m.ageMoyen || 0) * m.vehicules;
      m.tranches.forEach((x, i) => { t.tranches[i].n += x.n; t.tranches[i].v += x.v; });
    });
    return {
      ...t, ageMoyen: t.vehicules ? Math.round(t.jours / t.vehicules) : null,
      valorisation: t.valorises === 0 ? 'absente' : t.valorises < t.vehicules ? 'partielle' : 'ok',
    };
  };
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommerStock, maille.ouverts, (x) => x.vehicules),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);


  if (erreur) return <main className="cr-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>;
  if (!data) return <main className="cr-page"><div className="cr-skel" /></main>;
  if (societes.length === 0) {
    return (<main className="cr-page"><div className="bi-empty"><h2>Aucun véhicule en stock dans votre périmètre</h2><p>Le stock apparaîtra ici pour les sociétés que votre habilitation autorise.</p></div></main>);
  }

  const vide = selected.length === 0;
  // Dormant = au-delà de 120 jours, soit les deux dernières tranches. L'indice
  // suit BORNES côté service : quatre tranches, dormance à partir de la 3e.
  const dormants = total.tranches.slice(2).reduce((a, x) => ({ n: a.n + x.n, v: a.v + x.v }), { n: 0, v: 0 });
  const partiel = sel.filter((s) => s.valorisation !== 'ok');
  const dateArrete = data.arrete ? new Date(data.arrete).toLocaleDateString('fr-FR') : '—';
  const cell = (x) => (unite === 'n' ? (x.n || '·') : kc(x.v));
  const nomSel = selected.length === societes.length ? 'Toutes les sociétés' : selected.length === 1 ? sel[0]?.nom : `${selected.length} sociétés`;

  const K = [
    { l: 'Véhicules en stock', val: nf.format(total.vehicules), meta: nomSel },
    { l: 'Valorisation', val: cM(total.valeur), meta: total.valorises < total.vehicules ? `${nf.format(total.vehicules - total.valorises)} véh. sans valeur d'achat` : 'prix d\'achat, HT' },
    { l: 'Âge moyen', val: total.ageMoyen == null ? '—' : `${total.ageMoyen} j`, meta: 'jours depuis l\'entrée en stock' },
    { l: 'Stock dormant > 120 j', val: nf.format(dormants.n), meta: `${pct(dormants.n, total.vehicules)} % du volume · ${cM(dormants.v)}`, dot: HUES[2], alert: pct(dormants.n, total.vehicules) >= 30 },
    { l: '> 365 j', val: nf.format(total.tranches[3]?.n || 0), meta: cM(total.tranches[3]?.v || 0), dot: HUES[3], alert: (total.tranches[3]?.n || 0) > 0 },
  ];

  return (
    <>
      <main className="cr-page">
        <div className="cr-head">
          <div>
            <h1>{TITRE[type]}{branche !== 'auto-antilles' ? ` · ${BRANCHE_NOM[branche]}` : ''}</h1>
          </div>
          <div className="cr-asof">Arrêté au <b>{dateArrete}</b><br /><span>source stock Datacar · A97 Data</span></div>
        </div>

        {vide ? (
          <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="cr-kpis">
              {K.map((k, i) => (
                <div className={`cr-kpi${k.alert ? ' alert' : ''}`} key={i}>
                  <div className="cr-kpi-l">{k.l}</div>
                  <div className="cr-kpi-v">{k.val}</div>
                  <div className="cr-kpi-m">{k.meta}</div>
                </div>
              ))}
            </div>

            <div className="cr-card">
              <div className="cr-card-h st-card-h">
                <h2>Stock par concession</h2>
                <div className="st-tools">
                  <div className="st-seg">
                    <button type="button" className={unite === 'n' ? 'on' : ''} onClick={() => setUnite('n')}>véhicules</button>
                    <button type="button" className={unite === 'v' ? 'on' : ''} onClick={() => setUnite('v')}>k€</button>
                  </div>
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                </div>
              </div>
              <div className="cr-scroll">
                <table className="cr-tbl cr-tbl-soc st-tbl">
                  <thead><tr>
                    {/* Une seule colonne de tête : elle suit la bascule, comme
                        les tranches. Afficher les deux quelle que soit l'unité
                        faisait cohabiter deux grandeurs dans le même tableau. */}
                    <th>Société</th>
                    <th className="num">{unite === 'n' ? 'Véhicules' : 'Valorisation'}</th>
                    {type === 'vn' && <th className="num">dont VD</th>}
                    <th className="num">Âge moyen</th>
                    {labels.map((l, i) => <th key={i} className="num">{l}</th>)}
                  </tr></thead>
                  <tbody>
                    {arbre.map((s) => {
                      const cible = s.feuille ? s.code : (s.membres || []).join(',');
                      return (
                        <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${societesUrl === cible ? ' actif' : ''}`}
                            onClick={() => setFiltre(basculer(societesUrl, cible))}>
                          <td>
                            <span className="rg-in" style={{ width: s.niveau * 14 }} />
                            {s.feuille ? <span className="rg-chev vide" /> : (
                              <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                                title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par concession'}
                                aria-label="Déplier"
                                onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                            )}
                            {s.libelle || s.nom}
                            {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                          </td>
                          {unite === 'n'
                            ? <td className="num strong">{nf.format(s.vehicules)}</td>
                            : (
                              <td className={`num strong${s.valorisation === 'ok' ? '' : ' st-partiel'}`}
                                  title={s.valorisation === 'ok' ? '' : "véhicules sans valeur d'achat dans l'export"}>
                                {s.valorisation === 'absente' ? <span className="muted">non valorisé</span>
                                  : <>{cM(s.valeur)}{s.valorisation === 'partielle' && <sup>*</sup>}</>}
                              </td>
                            )}
                          {type === 'vn' && <td className={`num${s.vd?.n ? '' : ' muted'}`}>{cell(s.vd || { n: 0, v: 0 })}</td>}
                          <td className="num muted">{s.ageMoyen == null ? '—' : `${s.ageMoyen} j`}</td>
                          {s.tranches.map((x, i) => <td key={i} className={`num${x.n ? '' : ' muted'}`}>{cell(x)}</td>)}
                        </tr>
                      );
                    })}
                  </tbody>
                  {sel.length > 1 && (
                    <tfoot><tr>
                      <td>Total</td>
                      <td className="num strong">{unite === 'n' ? nf.format(total.vehicules) : <>{cM(total.valeur)}{partiel.length > 0 && <sup>*</sup>}</>}</td>
                      {type === 'vn' && <td className="num">{cell(total.vd)}</td>}
                      <td className="num muted">{total.ageMoyen == null ? '—' : `${total.ageMoyen} j`}</td>
                      {total.tranches.map((x, i) => <td key={i} className="num">{cell(x)}</td>)}
                    </tr></tfoot>
                  )}
                </table>
              </div>
              
            </div>

            <DetailStock type={type} branche={branche} selSet={selSet} labels={labels} dateArrete={dateArrete}
              filtres={filtresStock} setFiltres={setFiltresStock} />
          </>
        )}
      </main>
      {Filtres}
    </>
  );
}
